/**
 * MatryxTournament.js
 * Class for parsing values from MatryxTournament smart contract
 *
 * Authors dev@nanome.ai
 * Copyright (c) 2018, Nanome Inc
 * Licensed under ISC. See LICENSE.md in project root.
 */

import { fromWei, toWei } from ".."
import Contract from "./Contract"

export default class MatryxTournament extends Contract {
  async getInfo() {
    let info = await this.contract.getInfo()
    info.version = +info.version
    return info
  }

  async getDetails() {
    let details = await this.contract.getDetails()

    details.bounty = fromWei(details.bounty)
    details.entryFee = fromWei(details.entryFee)

    return details
  }

  async getBalance() {
    let balance = await this.contract.getBalance()
    return fromWei(balance)
  }

  async getState() {
    let states = {
      0: "scheduled", // first round not started
      1: "pending", // last round closed, next round not started
      2: "open",
      3: "closed",
      4: "abandoned"
    }
    let state = await this.contract.getState()
    return states[+state] || "broken"
  }

  async getRoundState(roundIndex) {
    let states = {
      0: "notYetOpen",
      1: "notFunded",
      2: "isOpen",
      3: "inReview",
      4: "hasWinners",
      5: "isClosed",
      6: "isAbandoned"
    }
    let state = await this.contract.getRoundState(roundIndex)
    return states[+state] || "broken"
  }

  async getCurrentRoundIndex() {
    let roundIndex = await this.contract.getCurrentRoundIndex()
    return +roundIndex
  }

  async getRoundInfo(roundIndex) {
    return await this.contract.getRoundInfo(roundIndex)
  }

  async getRoundDetails(roundIndex) {
    let details = await this.contract.getRoundDetails(roundIndex)

    details.start = +details.start
    details.duration = +details.duration
    details.review = +details.review
    details.bounty = fromWei(details.bounty)

    return details
  }

  async getSubmissionCount() {
    let count = await this.contract.getSubmissionCount()
    return +count
  }

  async getEntryFeePaid(userAddress) {
    let paid = await this.contract.getEntryFeePaid(userAddress)
    return +paid
  }

  async isEntrant(address) {
    return await this.contract.isEntrant(address)
  }

  // TRANSACTION FUNCTIONS BELOW

  async enter() {
    return await this.contract.enter()
  }

  async exit() {
    return await this.contract.exit()
  }

  async createSubmission({ content, commitHash }) {
    return await this.contract.createSubmission(content, commitHash)
  }

  async updateDetails({ content, entryFee }) {
    entryFee = toWei(entryFee)

    return await this.contract.updateDetails({
      content,
      bounty: 0,
      entryFee
    })
  }

  async addToBounty(amount) {
    amount = toWei(amount)
    return await this.contract.addToBounty(amount)
  }

  async transferToRound(amount) {
    amount = toWei(amount)
    return await this.contract.transferToRound(amount)
  }

  async selectWinners(winnersData, roundData) {
    // winnersData expects:
    //   submissions: submissionHash[]
    //   distribution: number[]
    //   action: number

    // roundData expects:
    //   duration: number (s)
    //   review: number (s)
    //   bounty: number (MTX)

    roundData = JSON.parse(JSON.stringify(roundData))

    if (winnersData.action === 1) {
      roundData.start = 0
      roundData.bounty = toWei(roundData.bounty)
    } else {
      roundData = [0, 0, 0, 0]
    }

    return await this.contract.selectWinners(winnersData, roundData)
  }

  async updateNextRound(roundData) {
    // roundData expects:
    //   start: date
    //   duration: number (s)
    //   review: number (s)
    //   bounty: number (MTX)

    roundData = JSON.parse(JSON.stringify(roundData))

    roundData.start = Math.floor(new Date(roundData.start) / 1000)
    roundData.bounty = toWei(roundData.bounty)

    return await this.contract.updateNextRound(roundData)
  }

  async startNextRound() {
    return await this.contract.startNextRound()
  }

  async closeTournament() {
    return await this.contract.closeTournament()
  }

  async withdrawFromAbandoned() {
    return await this.contract.withdrawFromAbandoned()
  }

  async recoverBounty() {
    return await this.contract.recoverBounty()
  }
}
